import React, { useMemo } from "react";
import { format, parseISO, isValid } from "date-fns";
import GlobalDataTable, { TableRow, TableCell } from "../../../../global/GlobalDataTable";
import GlobalDirectoryPaginationSection from "../../../../global/directory/GlobalDirectoryPaginationSection";
import { getVisitorColumnDefs } from "../../utils/visitorColumnDefs";
import RegisteredByCell from "./RegisteredByCell";
import VisitorStatusBadge from "./VisitorStatusBadge";
import VisitorActions from "./VisitorActions";
import "../../styles/visitorTable.css";
import "../../../user-builder/styles/userBuilderTable.css";

function formatDateCell(value) {
  if (!value) return "—";
  try {
    const d = typeof value === "string" && value.includes("T")
      ? parseISO(value)
      : parseISO(String(value).slice(0, 10));
    return isValid(d) ? format(d, "dd MMM yyyy") : String(value).slice(0, 10);
  } catch {
    return String(value).slice(0, 10);
  }
}

function formatTimeCell(value) {
  if (!value) return "—";
  const s = String(value);
  return s.length >= 5 ? s.slice(0, 5) : s;
}

function visitorIdCell(row) {
  return row.visitor_code || (row.id != null ? String(row.id) : "—");
}

function renderCell(columnId, row) {
  switch (columnId) {
    case "visitor_id":
      return visitorIdCell(row);
    case "entry_date":
      return formatDateCell(row.entry_date);
    case "visitor_name":
      return <span style={{ fontWeight: 500 }}>{row.visitor_name || "—"}</span>;
    case "mobile_number":
      return row.mobile_number || "—";
    case "purpose_name":
      return row.purpose_name || "—";
    case "person_to_meet_name":
      return row.person_to_meet_name || "—";
    case "in_time":
      return formatTimeCell(row.in_time);
    case "out_time":
      return formatTimeCell(row.out_time);
    case "created_by_name":
      return <RegisteredByCell row={row} />;
    case "status":
      return <VisitorStatusBadge visitor={row} />;
    case "actions":
      return null;
    default:
      return row[columnId] ?? "—";
  }
}

export default function VisitorTable({
  rows = [],
  loading = false,
  isAdmin = false,
  permissions,
  roleSlug = "",
  page = 1,
  pageSize = 15,
  total = 0,
  totalPages = 1,
  onPageChange,
  onView,
  onEdit,
  onDelete,
  onExit,
  onSlip,
  emptyMessage = "No visitors match your search.",
  columnVisibilityOverride = null,
  showPaginationFooter = true,
}) {
  const allColumns = getVisitorColumnDefs(isAdmin);

  const columns = useMemo(() => {
    if (!columnVisibilityOverride) return allColumns;
    return allColumns.filter((col) => {
      if (col.id === "actions") return true;
      return columnVisibilityOverride[col.id] !== false;
    });
  }, [allColumns, columnVisibilityOverride]);

  const headers = useMemo(
    () =>
      columns.map((c) => ({
        key: c.id,
        label: c.label,
        className: c.id === "actions" ? "ub-table__col-actions" : "",
      })),
    [columns],
  );

  if (loading) {
    return <div className="visitor-table-loading">Loading visitors…</div>;
  }

  if (!rows.length) {
    return (
      <div className="visitor-table-empty" role="status">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div>
      <GlobalDataTable headers={headers} tableAriaLabel="Visitors">
        {rows.map((row) => (
          <TableRow key={row.id}>
            {columns.map((col) =>
              col.id === "actions" ? (
                <TableCell key={col.id} isActions>
                  <VisitorActions
                    visitor={row}
                    permissions={permissions}
                    onView={onView}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onExit={onExit}
                    onSlip={onSlip}
                    compact
                  />
                </TableCell>
              ) : (
                <TableCell key={col.id}>{renderCell(col.id, row)}</TableCell>
              ),
            )}
          </TableRow>
        ))}
      </GlobalDataTable>

      {showPaginationFooter && totalPages > 1 ? (
        <GlobalDirectoryPaginationSection
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={pageSize}
          onPageChange={onPageChange}
          label="Visitor directory pagination"
        />
      ) : null}
    </div>
  );
}
